import React, { useEffect, useState, useCallback, useMemo } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { injectIntl } from 'react-intl';

import { hideLoading, showLoading } from 'app/store/slices/settings';
import {
    clearNewRequest,
    clearRequestDetails,
    fetchSaveRequest,
    updateRequestDetails,
} from 'app/store/slices/requests';
import notification from 'app/config/notification';
import { CircularProgress, makeStyles } from '@material-ui/core';

import { errorHandler } from 'app/utils/errorHandler';
import { SERVICES_CODES, SERVICES_GROUPS } from 'app/config/services';
import { clearServiceDetails, clearServiceDocuments, fetchServiceDocuments } from 'app/store/slices/services';
import { INTERNAL_CONTEXT } from 'app/config/urls';
import requestEndpoints from 'app/api/services/requestEndpoints';
import {
    clearAlertMessage,
    clearCity,
    clearDeletedVehicles,
    clearFiles,
    clearInternalNewRequest,
    clearVehicles,
} from 'app/store/slices/forms';

import { clearInspectedVehicles, clearVehiclePlateByCompany } from 'app/store/slices/vehicle';
import { clearActualRevision, clearRevisions } from 'app/store/slices/revisions';

import ChangeEmail from './components/ChangeEmail';
import Company from './components/Company';
import Vehicles from './components/Vehicles';
import Survey from './components/Survey';
import Licenses from './components/Licenses';
import Lines from './components/Lines';

import { preparePayload } from 'app/utils/forms/functions';
import { USER_SECTORS } from 'app/utils/constants';
import styles from './styles';
const useStyles = makeStyles(styles);

function Request(props) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();

    const { intl } = props;
    const [loading, setLoading] = useState(false);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const [cdSolicitacao, setCdSolicitacao] = useState(null);

    const authUser = useSelector((state) => state.auth.user);
    const documentsStore = useSelector((state) => state.services.documents);
    const internalNewRequest = useSelector((state) => state.forms.internalNewRequest);
    const request = useSelector((state) => state.requests.details);

    const isDplo = useMemo(() => {
        return !authUser.setores ? null : authUser.setores.find((e) => USER_SECTORS.DQS_DPLO === e.cdOrgaoSetor);
    }, [authUser]);

    useEffect(() => {
        return () => {
            dispatch(clearAlertMessage());
            dispatch(clearCity());
            dispatch(clearFiles());
            dispatch(clearServiceDetails());
            dispatch(clearServiceDocuments());
            dispatch(clearVehicles());
            dispatch(clearNewRequest());
            dispatch(clearRequestDetails());
            dispatch(clearRevisions());
            dispatch(clearActualRevision());
            dispatch(clearVehiclePlateByCompany());
            dispatch(clearInspectedVehicles());
            dispatch(clearDeletedVehicles());
            dispatch(clearInternalNewRequest());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const requestJsonData = useMemo(async () => {
        if (request.jsonSolicitacao) return JSON.parse(request.jsonSolicitacao);

        let obj = {};

        if (internalNewRequest && Object.keys(internalNewRequest).length > 0) {
            if (internalNewRequest.company) {
                obj = {
                    cdTipoEmpresa: internalNewRequest.company.cdTipoEmpresa,
                    deCpfCnpj:
                        internalNewRequest.company.deCnpjMatriz ||
                        internalNewRequest.company.deCpf ||
                        internalNewRequest.company.deCpfCnpj,
                    nmCompleto: internalNewRequest.company.deRazaoSocial || internalNewRequest.company.nmCompleto,
                };
            } else {
                obj = {
                    deCpfCnpj: internalNewRequest.user?.deCpfCnpj || internalNewRequest.user?.deCpf,
                    nmCompleto: internalNewRequest.user?.deRazaoSocial || internalNewRequest.user?.nmCompleto,
                };
            }
        }

        return obj;
    }, [internalNewRequest, request.jsonSolicitacao]);

    const resetAndGo = useCallback(() => {
        dispatch(hideLoading());
        dispatch(clearAlertMessage());
        dispatch(clearCity());
        dispatch(clearFiles());
        dispatch(clearServiceDetails());
        dispatch(clearServiceDocuments());
        dispatch(clearVehicles());
        dispatch(clearNewRequest());
        dispatch(clearRequestDetails());
        dispatch(clearRevisions());
        dispatch(clearActualRevision());
        dispatch(clearVehiclePlateByCompany());
        dispatch(clearInspectedVehicles());
        dispatch(clearDeletedVehicles());
        dispatch(clearInternalNewRequest());
        history.push(`${INTERNAL_CONTEXT}`);
    }, [dispatch, history]);

    useEffect(() => {
        const init = () => {
            if (Object.keys(internalNewRequest).length === 0) {
                history.push(`${INTERNAL_CONTEXT}`);
            }
        };
        init();
    }, [history, internalNewRequest]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            setLoadingDocuments(true);
            if (Object.keys(initialValues).length > 0) {
                const objPromisseDocuments = { cdServico: internalNewRequest.cdServico };
                if (initialValues.cdTipoPessoaFisica) {
                    objPromisseDocuments.cdTipoPessoaFisica = parseInt(initialValues.cdTipoPessoaFisica);
                }
                if (initialValues.cdTipoEmpresa) {
                    objPromisseDocuments.cdTipoEmpresa = parseInt(initialValues.cdTipoEmpresa);
                }
                await dispatch(fetchServiceDocuments(objPromisseDocuments));
            }
            setLoadingDocuments(false);
            setLoading(false);
        })();
        // eslint-disable-next-line
    }, [
        dispatch,
        initialValues.cdTipoEmpresa,
        initialValues.cdTipoPessoaFisica,
        internalNewRequest.cdServico,
        setLoading,
    ]);

    const onSave = useCallback(
        async (values, fromSubmit, oldData, setupPayload) => {
            let valuesCopy = { ...values };
            if (internalNewRequest.cdServico !== SERVICES_CODES.INSCRICAO_CADASTRAL) {
                if (!valuesCopy.cdCertificado && internalNewRequest?.company?.cdCertificado) {
                    valuesCopy.cdCertificado = internalNewRequest.company.cdCertificado;
                }
            }
            if (!valuesCopy.documentos) {
                //only aditional documents
                if (initialValues.documentos && initialValues.documentos.length > 0) {
                    valuesCopy.documentos = initialValues.documentos;
                }
            }

            if (documentsStore.length > 0 && valuesCopy.documentos?.length === 0) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.MISSING_MANDATORY_INFOS' }), {
                    toastId: 'MISSING_MANDATORY_INFOS',
                    type: 'warning',
                });
                return false;
            }

            //function from specific service save
            valuesCopy = setupPayload(valuesCopy, requestJsonData, oldData);
            if (!valuesCopy) return;

            const documentsObject = {
                store: documentsStore,
                files: valuesCopy.documentos,
            };

            let showMessage = false;
            let response = false;
            if (!fromSubmit) showMessage = true;

            try {
                let dataToPayload = {
                    cdServico: internalNewRequest.cdServico,
                    cdUsuarioSolicitacao: internalNewRequest.cdUsuario,
                    cdUsuarioRequerente: authUser.username,
                    cdUsuarioResponsavel: authUser.username,
                };

                if (isDplo) {
                    dataToPayload.cdUsuarioSolicitacao =
                        internalNewRequest.company.deCnpjMatriz ||
                        internalNewRequest.company.deCpf ||
                        internalNewRequest.company.deCpfCnpj ||
                        internalNewRequest.cdUsuario;
                }

                if (
                    internalNewRequest.company?.cdEmpresa &&
                    internalNewRequest.cdServico !== SERVICES_CODES.INSCRICAO_CADASTRAL
                ) {
                    dataToPayload.cdEmpresa = internalNewRequest.company.cdEmpresa;
                }

                if (cdSolicitacao) {
                    valuesCopy.cdSolicitacao = cdSolicitacao;
                }

                const preparedPayload = preparePayload({
                    values: valuesCopy,
                    documents: documentsObject,
                    params: dataToPayload,
                    activeCompany: '',
                    userAdmin: '',
                    registrationUserInfo: '',
                    isInternalNewRequest: true,
                });

                dispatch(showLoading());
                response = await fetchSaveRequest({
                    isFormData: true,
                    preparedPayload,
                    dispatch,
                    intl,
                    showMessage,
                    cdService: internalNewRequest.cdServico,
                });

                if (response.status === 200) {
                    await dispatch(updateRequestDetails(response.data));
                }
            } catch (error) {
                // notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.PROBLEM_SENDING_REQUEST' }), {
                //   toastId: 'PROBLEM_SENDING_REQUEST',
                //   type: 'error',
                // });
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            if (!fromSubmit) dispatch(hideLoading());
            return response;
        },
        [
            authUser.username,
            cdSolicitacao,
            dispatch,
            documentsStore,
            initialValues.documentos,
            internalNewRequest,
            isDplo,
            intl,
            requestJsonData,
        ],
    );

    const onSubmit = useCallback(
        async (values, onSave, setupPayload) => {
            try {
                const oldData = {};
                const responseSave = await onSave(values, true, oldData, setupPayload);
                if (responseSave && responseSave.status === 200) {
                    const cdSolicitacao = responseSave.data.cdSolicitacao;
                    setCdSolicitacao(cdSolicitacao);
                    const response = await requestEndpoints.finishRequest(cdSolicitacao);
                    if (response.status === 200) {
                        notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SUCCESSFUL_FINISHED_REQUEST' }), {
                            toastId: 'SUCCESSFUL_FINISHED_REQUEST',
                            type: 'success',
                        });

                        const requestResponse = await requestEndpoints.findRequest({ cdSolicitacao }, '');

                        if (response.status === 200) {
                            dispatch(updateRequestDetails(requestResponse.data));
                        }
                        resetAndGo();
                    }
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            dispatch(hideLoading());
        },
        [dispatch, intl, resetAndGo],
    );

    const onCancel = useCallback(() => {
        history.goBack();
    }, [history]);

    const renderRequest = () => {
        let Component = <></>;
        const internalCdServico = internalNewRequest.cdServico || request.cdServico;
        if (!internalCdServico) return Component;

        if (SERVICES_GROUPS.COMPANY.includes(internalCdServico)) {
            if (SERVICES_CODES.CHANGE_EMAIL === internalCdServico) {
                Component = ChangeEmail;
            } else {
                Component = Company;
            }
        } else if (SERVICES_GROUPS.VEHICLES.includes(internalCdServico)) {
            Component = Vehicles;
        } else if (SERVICES_GROUPS.SURVEY.includes(internalCdServico)) {
            Component = Survey;
        } else if (SERVICES_GROUPS.LICENSE.includes(internalCdServico)) {
            Component = Licenses;
        } else if (
            SERVICES_GROUPS.LINES_SLIC.includes(internalCdServico) ||
            SERVICES_GROUPS.LINES_STRIP.includes(internalCdServico)
        ) {
            Component = Lines;
        }

        return (
            <>
                {loading && (
                    <div className={classes.centerProgress}>
                        <CircularProgress size={20} />
                    </div>
                )}
                {!loading && (
                    <Component
                        {...props}
                        classes={classes}
                        onCancel={onCancel}
                        onSave={onSave}
                        onSubmit={onSubmit}
                        loading={loading}
                        setLoading={setLoading}
                        loadingDocuments={loadingDocuments}
                        setLoadingDocuments={setLoadingDocuments}
                        initialValues={initialValues}
                        setInitialValues={setInitialValues}
                    />
                )}
            </>
        );
    };

    return renderRequest();
}

export default injectIntl(Request);
